/**
 * The water stream (spec 02 §8): the ten cells the C64 draws (`waterCells`), each a block of
 * rippling water filling its tile, drawn with the bubbles' glass so it reads as water.
 */

import { BoxGeometry, type Material, Mesh, type Scene } from 'three';
import { waterCells } from '../../sim/bubbles/view';
import type { World } from '../../sim/types';
import { createGlassBubbleMaterial } from '../materials/glassBubble';
import type { Palette } from '../palette';
import { pixelToWorld } from './simToWorld';

const CELLS = 10;

export class WaterView {
  private readonly cells: Mesh[] = [];
  private readonly geometry = new BoxGeometry(1, 1, 1.1, 2, 2, 2);
  readonly material: Material;

  constructor(private readonly scene: Scene, p: Palette) {
    // Its own clear middle, not the bubbles' (which the 1987 look clears): water must read as water.
    this.material = createGlassBubbleMaterial({ tint: p.color('water.1'), rim: p.color('water.2'), depthBias: 2.5, body: 0.05 });
    for (let i = 0; i < CELLS; i++) {
      const m = new Mesh(this.geometry, this.material);
      m.visible = false;
      scene.add(m);
      this.cells.push(m);
    }
  }

  sync(world: World, t: number): void {
    const cells = waterCells(world);
    this.cells.forEach((m, i) => {
      const c = cells[i];
      m.visible = c !== undefined;
      if (!c) return;
      pixelToWorld(c.col * 8 + 4, c.row * 8 + 4, m.position);
      m.scale.y = 0.9 + Math.sin(t * 12 + i) * 0.08;
    });
  }

  clear(): void {
    for (const m of this.cells) m.visible = false;
  }

  dispose(): void {
    for (const m of this.cells) this.scene.remove(m);
    this.geometry.dispose();
  }
}
